export type Rng = () => number;

export type Item = {
  id: number;
  baseX: number;
  x: number;
  y: number;
  r: number;
  color: string;
  speed: number;
  swayPhase: number;
  swayAmp: number;
};

export type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  color: string;
};

export type State = {
  width: number;
  height: number;
  time: number;
  items: Item[];
  particles: Particle[];
  spawnTimer: number;
  popped: number;
  target: number;
  nextId: number;
  colors: string[];
  particleColors: string[];
  reducedMotion: boolean;
  rng: Rng;
};

export type StateOptions = {
  width: number;
  height: number;
  target: number;
  colors: string[];
  particleColors?: string[];
  reducedMotion?: boolean;
  rng?: Rng;
};

const SPAWN_INTERVAL = 0.7;
const MAX_ACTIVE = 6;
const INITIAL_ITEMS = 3;
const GRAVITY = 600;

export function itemRadius(width: number, height: number): number {
  return Math.min(Math.max(Math.min(width, height) * 0.08, 26), 48);
}

export function createState(options: StateOptions): State {
  const state: State = {
    width: options.width,
    height: options.height,
    time: 0,
    items: [],
    particles: [],
    spawnTimer: SPAWN_INTERVAL,
    popped: 0,
    target: options.target,
    nextId: 1,
    colors: options.colors,
    particleColors: options.particleColors ?? options.colors,
    reducedMotion: options.reducedMotion ?? false,
    rng: options.rng ?? Math.random,
  };
  for (let i = 0; i < INITIAL_ITEMS; i++) {
    const item = spawn(state);
    item.y = state.height * (0.35 + 0.2 * i);
  }
  return state;
}

export function isComplete(state: State): boolean {
  return state.popped >= state.target;
}

export function resize(state: State, width: number, height: number): void {
  const sx = state.width > 0 ? width / state.width : 1;
  const sy = state.height > 0 ? height / state.height : 1;
  const r = itemRadius(width, height);
  for (const item of state.items) {
    item.baseX *= sx;
    item.x *= sx;
    item.y *= sy;
    item.r = r;
    item.speed = riseSpeed(height, state.reducedMotion, state.rng);
  }
  state.width = width;
  state.height = height;
}

export function step(state: State, dt: number): void {
  state.time += dt;

  if (!isComplete(state)) {
    state.spawnTimer -= dt;
    if (state.spawnTimer <= 0) {
      state.spawnTimer = SPAWN_INTERVAL;
      if (state.items.length < MAX_ACTIVE) {
        spawn(state);
      }
    }
  }

  for (const item of state.items) {
    item.y -= item.speed * dt;
    item.x = state.reducedMotion ? item.baseX : item.baseX + Math.sin(state.time * 1.6 + item.swayPhase) * item.swayAmp;
  }
  state.items = state.items.filter((item) => item.y + item.r * 3 > 0);

  for (const p of state.particles) {
    p.life -= dt;
    p.vy += GRAVITY * dt;
    p.x += p.vx * dt;
    p.y += p.vy * dt;
  }
  state.particles = state.particles.filter((p) => p.life > 0);
}

export function popAt(state: State, x: number, y: number): boolean {
  if (isComplete(state)) {
    return false;
  }
  for (let i = state.items.length - 1; i >= 0; i--) {
    const item = state.items[i];
    const reach = item.r * 1.25 + 8;
    if (Math.hypot(item.x - x, item.y - y) <= reach) {
      pop(state, i);
      return true;
    }
  }
  return false;
}

export function popAny(state: State): boolean {
  if (isComplete(state)) {
    return false;
  }
  let best = -1;
  for (let i = 0; i < state.items.length; i++) {
    const item = state.items[i];
    const visible = item.y - item.r > 0 && item.y + item.r < state.height;
    if (visible && (best < 0 || item.y < state.items[best].y)) {
      best = i;
    }
  }
  if (best < 0) {
    return false;
  }
  pop(state, best);
  return true;
}

function pop(state: State, index: number): void {
  const [item] = state.items.splice(index, 1);
  state.popped += 1;
  const count = state.reducedMotion ? 6 : 14;
  for (let i = 0; i < count; i++) {
    const angle = (Math.PI * 2 * i) / count + state.rng() * 0.4;
    const speed = 120 + state.rng() * 180;
    const life = 0.5 + state.rng() * 0.4;
    state.particles.push({
      x: item.x,
      y: item.y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed - 120,
      life,
      maxLife: life,
      size: 3 + state.rng() * 4,
      color: state.particleColors[Math.floor(state.rng() * state.particleColors.length)],
    });
  }
}

function riseSpeed(height: number, reducedMotion: boolean, rng: Rng): number {
  const seconds = (reducedMotion ? 7 : 4.5) + rng() * 1.5;
  return height / seconds;
}

function spawn(state: State): Item {
  const r = itemRadius(state.width, state.height);
  const margin = r * 1.5;
  const baseX = margin + state.rng() * Math.max(state.width - margin * 2, 1);
  const item: Item = {
    id: state.nextId++,
    baseX,
    x: baseX,
    y: state.height + r * 1.5,
    r,
    color: state.colors[Math.floor(state.rng() * state.colors.length)],
    speed: riseSpeed(state.height, state.reducedMotion, state.rng),
    swayPhase: state.rng() * Math.PI * 2,
    swayAmp: r * 0.4,
  };
  state.items.push(item);
  return item;
}
